import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { PelicanLogo } from '../components/PelicanLogo';
import { Search, Upload as UploadIcon, BookOpen, Sparkles, ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { CURRICULA, HERO_ARTWORK_URL } from '../constants/curricula';

export const Home = () => {
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/resources?search=${encodeURIComponent(search)}`);
    }
  };

  const handleUploadClick = () => {
    navigate('/upload');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        {/* Hero */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent border border-border">
              <PelicanLogo className="h-4 w-4 text-primary" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-[0.1em] font-mono text-primary">Our School Symbol</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight text-foreground">
              Share Knowledge,
              <br />
              <span className="text-primary">Excel Together</span>
            </h1>
            <p className="text-base sm:text-lg text-foreground/80 leading-relaxed">
              For <span className="font-medium text-foreground">Ardingly College students'</span> future learning.
              <span className="font-medium text-foreground"> Preview, learn, review</span> — a digital legacy of revision notes
              shared across IGCSE, A-Level, and IB year groups. Suitable for both long-term learning and last-minute cramming.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button size="lg" onClick={handleUploadClick} data-testid="home-upload-cta" className="gap-2">
                <UploadIcon className="h-5 w-5" strokeWidth={2} />
                Upload Your Notes
              </Button>
              <Link to="/subjects" data-testid="home-browse-button">
                <Button size="lg" variant="outline" className="gap-2">
                  <BookOpen className="h-5 w-5" strokeWidth={1.5} />
                  Browse Resources
                </Button>
              </Link>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <div className="relative w-full max-w-md aspect-square rounded-3xl overflow-hidden shadow-xl border border-border bg-gradient-to-br from-emerald-50 via-white to-teal-50 dark:from-slate-900 dark:via-slate-900 dark:to-teal-950">
              {/* Hero artwork — pelicans + academic doodles */}
              <img
                src={HERO_ARTWORK_URL}
                alt="ArdNotes — pelicans and revision motifs"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Soft mint wash to keep the artwork on-brand */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-primary/5 mix-blend-multiply dark:mix-blend-screen pointer-events-none" />
              {/* Bottom glass label */}
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between gap-3 p-3.5 rounded-xl bg-white/85 dark:bg-slate-900/85 backdrop-blur-md border border-border shadow-sm">
                <div className="flex items-center gap-2">
                  <PelicanLogo className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  <span className="text-xs uppercase tracking-[0.18em] font-mono text-foreground/80">ArdNotes</span>
                </div>
                <span className="text-[10px] tracking-wider uppercase font-mono text-foreground/55">IGCSE · A-Level · IB</span>
              </div>
            </div>
          </div>
        </div>

        {/* CHOOSE YOUR CURRICULUM — primary navigation */}
        <div className="mt-20" data-testid="curriculum-section">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent border border-border mb-4">
              <span className="text-xs uppercase tracking-[0.1em] font-mono text-primary">Step 1</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-3">Choose Your Curriculum</h2>
            <p className="text-foreground/70 max-w-xl mx-auto leading-relaxed">
              Pick the qualification you're studying — we'll show you the resources that match.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {CURRICULA.map((c) => (
              <Link
                key={c.name}
                to={`/curriculum/${encodeURIComponent(c.name)}`}
                data-testid={`curriculum-card-${c.name.toLowerCase()}`}
                className="group"
              >
                <div className={`relative h-full p-8 rounded-2xl border-2 ${c.light} hover:-translate-y-1 transition-all duration-300 hover:shadow-md overflow-hidden`}>
                  <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full bg-gradient-to-br ${c.accent} opacity-20 blur-2xl group-hover:opacity-30 transition-opacity`} />
                  <div className="relative">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-3xl font-semibold tracking-tight">{c.name}</h3>
                      <ArrowRight className="h-5 w-5 text-foreground/40 group-hover:text-primary group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
                    </div>
                    <p className="text-sm text-foreground/75 leading-relaxed mb-4">{c.description}</p>
                    {c.levels && (
                      <div className="flex gap-2">
                        {c.levels.map((lvl) => (
                          <span key={lvl} className={`text-xs px-2 py-1 rounded font-mono ${c.badge}`}>{lvl}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* PROMINENT UPLOAD CTA STRIP */}
        <div className="mt-16">
          <div className="relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-8 sm:p-10">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-primary text-white flex items-center justify-center">
                  <UploadIcon className="h-7 w-7" strokeWidth={2} />
                </div>
                <div>
                  <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-1">Got notes? Share them now.</h2>
                  <p className="text-foreground/75 leading-relaxed">
                    Upload your revision notes, past papers, or study guides in PDF, DOCX, PNG, or JPG. It takes less than a minute.
                  </p>
                </div>
              </div>
              <Button
                size="lg"
                onClick={handleUploadClick}
                data-testid="home-upload-cta-banner"
                className="gap-2 flex-shrink-0"
              >
                <UploadIcon className="h-5 w-5" strokeWidth={2} />
                Upload Now
              </Button>
            </div>
          </div>
        </div>

        {/* About — embellished with artwork strip */}
        <div className="mt-20 max-w-4xl mx-auto" data-testid="about-section">
          <div className="relative rounded-3xl border border-border bg-surface overflow-hidden">
            {/* Top decorative strip with the pelican artwork */}
            <div className="relative h-28 sm:h-32 overflow-hidden border-b border-border">
              <img
                src={HERO_ARTWORK_URL}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-surface" />
            </div>

            <div className="p-8 sm:p-12">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accent border border-border mb-4 -mt-20 sm:-mt-24 shadow-md">
                  <PelicanLogo className="h-10 w-10 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4">About ArdNotes</h2>
              </div>
              <div className="space-y-5 text-base sm:text-lg leading-relaxed text-foreground/85">
                <p>
                  <span className="font-medium text-foreground">ArdNotes</span> is a collaborative revision platform built around the idea that valuable knowledge should never disappear after exams.
                </p>
                <p>
                  Built by <span className="font-medium text-foreground">Ardingly College students</span> on a simple cycle —
                  <span className="font-medium text-foreground"> preview</span> before class,
                  <span className="font-medium text-foreground"> learn</span> in class, and
                  <span className="font-medium text-foreground"> review</span> after — ArdNotes turns every student's notes
                  into a <span className="italic">digital legacy</span> that lives on for the year groups that follow.
                </p>
                <p>
                  Whether you're building a long-term study habit or cramming the night before, ArdNotes works for both.
                  Search by curriculum and subject, find what you need, and add your own contribution to the legacy.
                </p>
                <p className="border-l-2 border-primary pl-5 text-foreground/90">
                  Every upload helps future learners revise better, learn smarter, and build a habit that lasts.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mt-20">
          <form onSubmit={handleSearch} className="max-w-3xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-foreground/60" strokeWidth={1.5} />
              <Input
                type="text"
                placeholder="Search for resources, subjects, or topics..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 pr-28 h-14 text-base"
                data-testid="home-search-input"
              />
              <Button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2"
                data-testid="home-search-button"
              >
                Search
              </Button>
            </div>
          </form>
        </div>

        {/* Final CTA */}
        <div className="mt-20">
          <div className="rounded-2xl border border-border bg-surface p-10 sm:p-14 text-center">
            <Sparkles className="h-8 w-8 text-primary mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4">Ready to start learning?</h2>
            <p className="text-foreground/75 mb-8 max-w-2xl mx-auto leading-relaxed">
              Join our community of students helping each other succeed. Upload your notes, download resources, and collaborate.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button size="lg" onClick={handleUploadClick} data-testid="home-cta-upload" className="gap-2">
                <UploadIcon className="h-5 w-5" strokeWidth={2} />
                Upload Notes
              </Button>
              <Link to="/subjects">
                <Button size="lg" variant="outline">Browse by Subject</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <footer className="border-t border-border mt-20 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center gap-2 text-sm text-foreground/60">
          <div className="flex items-center gap-2">
            <PelicanLogo className="h-5 w-5 text-primary" strokeWidth={1.5} />
            <span>ArdNotes — For Ardingly College students' future learning.</span>
          </div>
          <span className="text-[10px] tracking-[0.2em] uppercase text-foreground/25 font-mono select-none">create by z.z.y</span>
        </div>
      </footer>
    </div>
  );
};
